/**
 * Ngwaahia storefront icon — web SVG version.
 * Paths are identical to the React Native version in ngwaahia-mobile/components/ui/NgwaahiaIcon.js.
 *
 * Props:
 *   size     number   px (width = height). Default 28.
 *   color    string   any CSS color. Default brand cyan #00B1F6.
 *   variant  'filled' | 'outline'. Default 'filled'.
 */

const VIEWBOX = '142.67 141.9 300 300';

const BODY =
  'M193.11,241.8c-0.61,23.99-0.05,48.95-0.03,73.04c0.01,24.5-0.01,48.99,0,73.49c0,9.94-1.64,26.72,3.01,33.1c2.34,3.21,5.87,6.15,11.07,6.21c28.01,0.31,57.22-0.02,85.35-0.01l84.55,0c6.43,0.03,9.21-1.99,11.98-5.68c3.14-4.17,3.3-8.09,3.3-14.61c-0.06-55.08-0.4-110.69,0.01-165.67c-12.07-0.57-18.08-6.46-24.23-15.14c-11.49-16.22-34.09-19.34-45.79-5.37c-1.85,2.21-2.31,3.83-3.91,6.35c-12.56,19.78-39.91,17.95-51.51,0.95c-3.47-5.08-1.84-5.75-8.7-11.32c-4.23-3.43-8.44-4.69-15.01-4.7c-21.71-0.05-25.12,18.19-34.91,24.61C204.57,239.48,198.23,241.54,193.11,241.8L193.11,241.8z';
const SMILE =
  'M322.83,408.62c-3.23,3.37-13.82,7.21-20.94,8.5c-9.36,1.68-19.62,0.45-27.24-2.32c-4.07-1.48-15.27-5.09-11.61-8.25c5.66-4.87,6.58,5.84,31.28,5.84c12.26,0,19.64-5.45,23.25-6.82C320.23,404.55,322.89,406.14,322.83,408.62L322.83,408.62z';
const DASH =
  'M266.35,254.05h52.45c1.75,0,3.19,1.43,3.19,3.19v3.64c0,1.75-1.43,3.19-3.19,3.19h-52.45c-1.75,0-3.19-1.43-3.19-3.19v-3.64C263.16,255.48,264.6,254.05,266.35,254.05L266.35,254.05z';
const WINDOW =
  'M247.72,270.91h89.71c5.91,0,10.75,4.84,10.75,10.75v105.73c0,5.91-4.84,10.75-10.75,10.75h-89.71c-5.91,0-10.75-4.84-10.75-10.75V281.66C236.97,275.74,241.81,270.91,247.72,270.91z';
const AWNING = [
  'M248.68,156.02l-27.79,0.02c-2.69,0.48-19.62,20.56-23.71,24.67c-6.38,6.4-12.28,13.66-18.38,20.31c-3.77,4.1-6.69,6.24-6.93,12.86c-0.68,18.25,19.42,26.24,33.24,15.94c5.85-4.37,11.36-15.98,15.41-23.51c2.26-4.19,4.78-8.17,7.03-12.25C234.41,181.63,242.12,168.47,248.68,156.02z',
  'M393.89,234.26l0.39,0c10.65,0.05,19.18-7.94,19.2-19.09c0.01-7.93-3.82-10.55-10.05-17.47l-32.14-35.43c-1.47-1.59-5.35-6.25-7.53-6.25l-27.22,0.16c0.75,0.9,2.03,3.61,2.85,5.04c1.12,1.95,2.03,3.55,3.14,5.47c2.15,3.75,4.09,7.26,6.22,11.02l24.78,43.37C377.55,228.16,383.04,234.2,393.89,234.26z',
  'M300.73,156.05l-16.82-0.01c-0.75,6.85-3.55,16.66-5.01,23.36l-7.49,35.42c-1.21,5.9-0.8,6.88,2.15,10.68c9.53,12.3,32.32,13,40.35-4.59c0.93-2.03,0.19-3.34-0.28-5.69L300.73,156.05z',
  'M320.3,213.08c4.8-3.68,9.81-6.78,16.96-7.86c7.15-1.08,15.75,0.72,20.98,3.52c-0.26-1.24-13.08-22.91-15.12-26.63l-13.4-23.13c-0.74-1.33-1.17-2.72-2.37-2.95l-19.98,0c3.02,12.5,9.39,40.06,11.86,53.54l0.64,2.71C320.11,212.86,320.18,212.9,320.3,213.08z',
  'M227.58,208.76c4.58-2.95,13.92-4.39,20.23-3.55c8.1,1.08,12.12,4.26,17.17,8.13c1.28-4.38,2.09-9.42,3.04-14.05c1.01-4.9,2.05-9.55,3.1-14.39l6.29-28.87l-20.49,0L227.58,208.76z',
];

const FILLED_PATHS = [BODY + SMILE + DASH + WINDOW, ...AWNING];
const OUTLINE_INNER = [...AWNING, WINDOW, DASH, SMILE];

const NgwaahiaIcon = ({ size = 28, color = '#00B1F6', variant = 'filled', strokeWidth, innerStrokeWidth, ...rest }) => {
  const sw = strokeWidth ?? size * (11 / 28);
  const isw = innerStrokeWidth ?? size * (7 / 28);
  const outline = variant === 'outline' || variant === 'outlined' || variant === 'hollow';

  if (outline) {
    return (
      <svg width={size} height={size} viewBox={VIEWBOX} fill="none" {...rest}>
        <g fill="none" stroke={color} strokeLinejoin="round" strokeLinecap="round">
          {OUTLINE_INNER.map((d, i) => (
            <path key={`i${i}`} d={d} strokeWidth={isw} />
          ))}
          <path d={BODY} strokeWidth={sw} />
        </g>
      </svg>
    );
  }

  return (
    <svg width={size} height={size} viewBox={VIEWBOX} fill="none" {...rest}>
      <g fill={color}>
        {FILLED_PATHS.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </g>
    </svg>
  );
};

export default NgwaahiaIcon;
